import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from '@/features/auth/hooks/useSession';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query/keys';
import type { BoardArchiveConfirmCardData } from './types';

/**
 * "Archive this board?" card for a chat-proposed board removal ("delete my sales
 * board"). Boards are ARCHIVED, never hard-deleted — the agent emits this card
 * and touches nothing; the archive happens HERE, on a real user click, by reading
 * the module's full config, stamping `archivedAt` on the board, and writing the
 * config back. The model can never bypass the prompt. Reversible: the board moves
 * to Archived Boards (restorable) and its tasks are kept with it.
 *
 * Self-contained (mirrors BulkDeleteConfirmCard's local-writeback pattern).
 */
type Phase = 'idle' | 'archiving' | 'done' | 'cancelled' | 'error';

type Resolution = { status: 'archived' | 'cancelled' };

/** localStorage key for this card's resolved state, so a reload (incl. hard
 *  refresh) keeps the resolved state instead of re-showing the prompt. Keyed on
 *  the server-stamped `cardId` (unique per emit) so a later re-ask gets a fresh
 *  card; falls back to workspace+board when no cardId. */
function boardArchiveResolutionKey(data: { cardId?: string; workspaceId: string; boardId: string }): string {
  if (data.cardId) return `xavior:board-archive:${data.cardId}`;
  return `xavior:board-archive:${data.workspaceId}:${data.boardId}`;
}

function readResolution(key: string): Resolution | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Resolution;
    return parsed?.status === 'archived' || parsed?.status === 'cancelled' ? parsed : null;
  } catch {
    return null;
  }
}

function writeResolution(key: string, value: Resolution): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full/blocked — non-fatal, the card just won't survive reload */
  }
}

function ArchiveIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
      <path d="M10 12h4" />
    </svg>
  );
}

export function BoardArchiveConfirmCard({ data }: { data: BoardArchiveConfirmCardData }) {
  const { token } = useSession();
  const qc = useQueryClient();

  const { workspaceId, moduleId, boardId } = data;
  const boardName = data.boardName || 'this board';
  const previewTasks = Array.isArray(data.previewTasks) ? data.previewTasks : [];
  const taskCount = typeof data.taskCount === 'number' ? data.taskCount : previewTasks.length;
  const remaining = taskCount - previewTasks.length;
  const storageKey = boardArchiveResolutionKey({ cardId: data.cardId, workspaceId, boardId });

  const [phase, setPhase] = useState<Phase>(() => {
    const r = readResolution(storageKey);
    return r?.status === 'cancelled' ? 'cancelled' : r?.status === 'archived' ? 'done' : 'idle';
  });

  if (!workspaceId || !moduleId || !boardId) return null;

  async function handleConfirm() {
    setPhase('archiving');
    try {
      // Read the module's FULL, current config so the wholesale config write
      // preserves every other board (the API replaces module.config outright).
      const modules = await api.workspaces.listWorkspaceModules(token ?? null, workspaceId, { includeArchived: true });
      const mod = modules.find((m) => m.id === moduleId);
      const config = (mod?.config ?? {}) as Record<string, unknown>;
      const boards = Array.isArray(config.boards) ? (config.boards as Array<Record<string, unknown>>) : [];

      let found = false;
      const nextBoards = boards.map((b) => {
        if (String(b.id ?? '') !== boardId) return b;
        found = true;
        // Idempotent: keep an existing archivedAt if it's somehow already set.
        return { ...b, archivedAt: b.archivedAt ?? new Date().toISOString() };
      });

      // Already gone/archived — treat as success (nothing to do).
      if (found) {
        const nextConfig: Record<string, unknown> = { ...config, boards: nextBoards };
        // Don't leave the archived board as the active one, or the UI reopens it.
        if (config.activeBoardId === boardId) nextConfig.activeBoardId = null;
        await api.workspaces.updateWorkspaceModule(token ?? null, workspaceId, moduleId, { config: nextConfig });
        qc.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
      }

      writeResolution(storageKey, { status: 'archived' });
      setPhase('done');
    } catch {
      setPhase('error');
    }
  }

  if (phase === 'cancelled') {
    return (
      <div className="rc-bulkdelete">
        <div className="rc-bd-result is-muted">Cancelled — the board was not archived.</div>
      </div>
    );
  }

  if (phase === 'done') {
    return (
      <div className="rc-bulkdelete">
        <div className="rc-bd-result">Archived “{boardName}”. Restore it anytime from Archived Boards.</div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="rc-bulkdelete">
        <div className="rc-bd-result">Couldn’t archive “{boardName}” — please try again.</div>
      </div>
    );
  }

  const busy = phase === 'archiving';

  return (
    <div className="rc-bulkdelete" role="group" aria-label={`Confirm archiving the ${boardName} board`}>
      <div className="rc-bd-header">
        <span className="rc-bd-icon"><ArchiveIcon /></span>
        <span className="rc-bd-title">Archive “{boardName}”?</span>
      </div>

      {previewTasks.length > 0 && (
        <ul className="rc-bd-list">
          {previewTasks.map((t) => (
            <li key={t.id} className="rc-bd-item">
              <span className="rc-bd-item-title">{t.title}</span>
            </li>
          ))}
          {remaining > 0 && <li className="rc-bd-more">…and {remaining} more</li>}
        </ul>
      )}

      <div className="rc-bd-note">
        {taskCount > 0
          ? `Moves the board and its ${taskCount} task${taskCount === 1 ? '' : 's'} to Archived Boards — restore anytime.`
          : 'Moves the board to Archived Boards — restore anytime.'}
      </div>

      <div className="rc-bd-actions">
        <button
          type="button"
          className="rc-bd-btn rc-bd-cancel"
          onClick={() => {
            writeResolution(storageKey, { status: 'cancelled' });
            setPhase('cancelled');
          }}
          disabled={busy}
        >
          Cancel
        </button>
        <button type="button" className="rc-bd-btn rc-bd-delete" onClick={handleConfirm} disabled={busy}>
          {busy ? 'Archiving…' : 'Archive board'}
        </button>
      </div>
    </div>
  );
}
